import { applyUplift } from '../money/cents';
import type { Cents } from '../money/cents';
import type { RatePpm } from '../money/rate';

/**
 * What the web shop shows (SCOPE §9.2, decisions 143 and 148).
 *
 * Three rules the storefront and the API must agree on to the letter: the
 * address a style lives at, whether the web may sell it, and which price the
 * shopper is quoted.
 */

/** A style's address on the web: lower case, words joined by hyphens. */
export function toSlug(text: string): string {
  return text
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80);
}

/**
 * The slug a style is first offered: its name, then its style number, because
 * two colourways of "Cornice Jacket" from different seasons are different
 * pages and the number is what tells them apart.
 */
export function styleSlug(style: { name: string; styleNumber: string }): string {
  const name = toSlug(style.name);
  const number = toSlug(style.styleNumber);
  // invariant-ok(model): a web address, kept so published addresses do not move
  if (name === '') return number === '' ? 'style' : number;
  return number === '' || name.endsWith(number) ? name : `${name}-${number}`;
}

/** Make a slug unique for a store by adding the smallest number that is free. */
export function freeSlug(wanted: string, taken: ReadonlySet<string>): string {
  if (!taken.has(wanted)) return wanted;
  for (let suffix = 2; suffix < 1000; suffix += 1) {
    const candidate = `${wanted}-${suffix}`;
    if (!taken.has(candidate)) return candidate;
  }
  return `${wanted}-${Date.now()}`;
}

export type OnlineStock = 'in_stock' | 'out_of_stock';

/**
 * What the web may sell of one size.
 *
 * On hand, less everything held (layaways, carts, orders), less the buffer the
 * shop keeps back — **at or under the buffer the web says nothing is
 * available**, while the counter keeps selling the exact count (decision 143).
 * The buffer is what stops the website selling the last jacket out from under
 * the register.
 */
export function onlineAvailable(input: {
  onHand: number;
  reserved: number;
  buffer: number;
}): number {
  return Math.max(0, input.onHand - input.reserved - Math.max(0, input.buffer));
}

export function onlineStock(input: {
  onHand: number;
  reserved: number;
  buffer: number;
}): OnlineStock {
  return onlineAvailable(input) > 0 ? 'in_stock' : 'out_of_stock';
}

/** Which price a shopper is quoted when the shop posts two (SCOPE §8.2). */
export type OnlinePriceBasis = 'card' | 'cash' | 'both';

export interface OnlinePrice {
  /** What the shopper pays online — always the card price when there are two. */
  readonly payableCents: Cents;
  /** Shown as the headline price. */
  readonly shownCents: Cents;
  /** Shown beside it, when the shop posts both. */
  readonly alsoShownCents: Cents | null;
  /**
   * Said beside the price only when it is not what is charged: the cash price
   * shown, the card price charged. The card price is never labelled (decision
   * 261; client, 2026-10-29: "in the online store we don't need to show 'Card
   * price' next to the price that we display") — it is simply the price.
   */
  readonly label: 'cash' | null;
}

/**
 * Online is a card sale, so the card price is what is charged whatever is
 * shown — and the headline follows the shop's setting (decision 143).
 */
export function onlinePrice(input: {
  cashCents: Cents;
  dualPricing: { enabled: boolean; ratePpm: RatePpm };
  basis: OnlinePriceBasis;
}): OnlinePrice {
  if (!input.dualPricing.enabled) {
    return {
      payableCents: input.cashCents,
      shownCents: input.cashCents,
      alsoShownCents: null,
      label: null,
    };
  }
  const card = applyUplift(input.cashCents, input.dualPricing.ratePpm);
  if (input.basis === 'cash') {
    return { payableCents: card, shownCents: input.cashCents, alsoShownCents: null, label: 'cash' };
  }
  if (input.basis === 'both') {
    return {
      payableCents: card,
      shownCents: card,
      alsoShownCents: input.cashCents,
      label: null,
    };
  }
  return { payableCents: card, shownCents: card, alsoShownCents: null, label: null };
}

/**
 * A model's own price online (decision 259; client, 2026-10-27: "we also need
 * the ability to show a sale price on line that is different from the sale
 * price instore"; "a different online price per model … they will have a
 * store price that is likely lower than what they show online and online may
 * be discounted from MSRP … store price is never shown online if an online
 * price is set or exists (if its blank its the same as instore)"; "start end
 * dates are good").
 *
 * One price for every size of the model, higher or lower than the store's,
 * from a day and to a day when given — both the store's days, both inclusive.
 * Out of its dates, or blank, the web sells at the store price.
 */
export interface WebPrice {
  readonly cents: number | null;
  /** YYYY-MM-DD, the store's day it starts; null: already. */
  readonly startsOn: string | null;
  /** YYYY-MM-DD, the last day it applies; null: until it is taken off. */
  readonly endsOn: string | null;
}

export const NO_WEB_PRICE: WebPrice = { cents: null, startsOn: null, endsOn: null };

/** Whether the model's own online price applies on the store's day `today`. */
export function webPriceInEffect(price: WebPrice, today: string): boolean {
  if (price.cents === null || price.cents <= 0) return false;
  if (price.startsOn !== null && today < price.startsOn) return false;
  if (price.endsOn !== null && today > price.endsOn) return false;
  return true;
}

/**
 * What a size sells for online, before any card price: the model's online
 * price while it applies, else the store's. A size the store has not priced
 * is not for sale anywhere, online price or not.
 */
export function onlineCashCents(storeCents: number, price: WebPrice, today: string): number {
  if (storeCents <= 0) return storeCents;
  return webPriceInEffect(price, today) ? price.cents! : storeCents;
}

/**
 * The MSRP shown struck through beside an online price (the client: "price
 * struck through but MSRP probably, not store price"): only while the model's
 * own online price applies, and only when the MSRP is above what is shown.
 */
export function struckMsrp(
  msrpCents: number | null,
  shownCents: number,
  inEffect: boolean,
): number | null {
  return inEffect && msrpCents !== null && msrpCents > shownCents ? msrpCents : null;
}

/** What is wrong with an online price as typed, in words — or null. */
export function webPriceProblem(price: WebPrice): string | null {
  if (price.cents === null) {
    return price.startsOn !== null || price.endsOn !== null
      ? 'Give the online price, or clear its dates'
      : null;
  }
  if (!Number.isInteger(price.cents) || price.cents <= 0) {
    return 'An online price is more than $0.00 — leave it blank to sell at the store price';
  }
  if (price.startsOn !== null && price.endsOn !== null && price.endsOn < price.startsOn) {
    return 'The last day is before the first';
  }
  return null;
}
